import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Plan allowances: listings, expiry and boosts.
 *
 * The pricing page sells a number of listings a year, a length of time each
 * listing stays live, and a number of free boosts a month. All three are
 * enforced in the database — see the listings_enforce_allowance trigger and
 * boost_listing() — so this file only reads the position and shows it.
 *
 * Nothing here is the gate. If any of these calls fail the database still
 * refuses, which is the right way round.
 */

export interface ListingAllowance {
  signedIn: boolean;
  tier?: string;
  planName?: string;
  /** null = unlimited */
  limit: number | null;
  used: number;
  /** null = unlimited */
  remaining: number | null;
  /** How many days a new listing will stay live. null = no expiry. */
  listingDays?: number | null;
}

export interface BoostAllowance {
  signedIn: boolean;
  tier?: string;
  /** null = unlimited, 0 = plan has no boosts */
  limit: number | null;
  used: number;
  /** null = unlimited */
  remaining: number | null;
}

export interface BoostResult {
  boosted: boolean;
  /** 'plan' = the plan includes none, 'limit' = this month's are used up. */
  reason?: 'plan' | 'limit';
  until?: string;
  tier?: string;
  limit?: number;
  used?: number;
}

export async function fetchListingAllowance(
  supabase: SupabaseClient
): Promise<ListingAllowance> {
  const { data, error } = await supabase.rpc('listing_allowance');
  if (error || !data) return { signedIn: false, limit: null, used: 0, remaining: null };
  return data as ListingAllowance;
}

export async function fetchBoostAllowance(
  supabase: SupabaseClient
): Promise<BoostAllowance> {
  const { data, error } = await supabase.rpc('boost_allowance');
  if (error || !data) return { signedIn: false, limit: 0, used: 0, remaining: 0 };
  return data as BoostAllowance;
}

/** Boost one of your own live listings. Days defaults to a week. */
export async function boostListing(
  supabase: SupabaseClient,
  listingId: string,
  days = 7
): Promise<BoostResult> {
  const { data, error } = await supabase.rpc('boost_listing', {
    listing: listingId,
    days,
  });
  if (error) throw error;
  return data as BoostResult;
}

/**
 * Sweep out anything whose date has passed.
 *
 * Called when the marketplace loads rather than on a schedule, so there is no
 * cron job to go stale on the server. Both calls only touch rows that are
 * already overdue, and a failure is deliberately swallowed: a member browsing
 * listings should never see an error because a housekeeping call did not land.
 */
export async function expireDue(supabase: SupabaseClient): Promise<void> {
  try {
    await Promise.all([
      supabase.rpc('expire_due_listings'),
      supabase.rpc('expire_due_boosts'),
    ]);
  } catch {
    /* housekeeping only — never block the page */
  }
}

/** "4 of 6 listings left this year" / "Unlimited listings". */
export function listingAllowanceLabel(a: ListingAllowance): string {
  if (a.limit === null) return 'Unlimited listings on your plan';
  if (a.remaining === 0) return `You have used all ${a.limit} listings on your plan this year`;
  return `${a.remaining} of ${a.limit} listings left this year`;
}

/** "1 of 1 boost left this month" / "Boosts not included on your plan". */
export function boostAllowanceLabel(a: BoostAllowance): string {
  if (a.limit === null) return 'Unlimited boosts';
  if (a.limit === 0) return 'Boosts are not included on your plan';
  if (a.remaining === 0) {
    // Was hardcoded to "both", which read wrong the moment the Pro allowance
    // changed from 2 a month to 1.
    return a.limit === 1
      ? 'You have used your free boost this month'
      : `You have used all ${a.limit} free boosts this month`;
  }
  return `${a.remaining} of ${a.limit} boost${a.limit === 1 ? '' : 's'} left this month`;
}

/** How long a new listing will run for, in the words the pricing page uses. */
export function listingLengthLabel(days: number | null | undefined): string {
  if (!days) return 'Your listing will not expire';
  const weeks = Math.round(days / 7);
  if (days % 7 === 0 && weeks > 1) return `Your listing will stay live for ${weeks} weeks`;
  return `Your listing will stay live for ${days} days`;
}

/**
 * Start a purchase and hand the member to Stripe Checkout.
 *
 * Returns the URL to send them to. Nothing is granted here — the webhook does
 * that when the payment lands — so a member who abandons checkout has bought
 * nothing and been charged nothing.
 */
export async function startPurchase(
  input:
    | { kind: 'boost'; listingId: string; days?: number }
    | { kind: 'haulage_job'; job: Record<string, unknown> }
): Promise<string> {
  const res = await fetch('/api/purchase', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    throw new Error(data.error || 'Could not start that payment.');
  }
  return data.url as string;
}

/** "£20" from pence, without trailing pennies when it is a round pound. */
export function poundsLabel(pence: number): string {
  const v = (pence ?? 0) / 100;
  return `£${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
}
